import { AlertCircle, Info, Route } from "lucide-react";
import { Link } from "react-router-dom";
import type { HotspotBy, HotspotList } from "@iamu/shared";
import { EmptyState } from "@/components/empty-state";
import { ScopeEmptyState } from "@/components/findings-scope/scope-empty-state";
import { useFirstRunCopy } from "@/lib/project-access";
import { KEY_LABEL, plural } from "./hotspot-vocabulary";

export function CoverageNote({
  projectId,
  data,
  by,
}: {
  projectId: string;
  data: HotspotList;
  by: HotspotBy;
}) {
  const c = data.coverage[by];
  if (c.keyed >= c.total && by !== "route") return null;
  const text =
    by === "route"
      ? c.keyed === 0
        ? `Page grouping matches step URLs against route patterns. 0 of ${plural(c.total, "step")} in this window have a recorded page URL.`
        : c.keyed < c.total
          ? `${c.keyed} of ${plural(c.total, "step")} have a matched page; steps without one aren't counted here.`
          : "Every step in this window has a page."
      : by === "human_test"
        ? `${c.keyed} of ${plural(c.total, "finding")} are cited by a judge verdict. The rest are under "Not cited by any human test".`
        : `${c.keyed} of ${plural(c.total, "step")} belong to a ${KEY_LABEL[by].toLowerCase()}.`;
  return (
    <p className="flex items-start gap-2 rounded-sm border bg-muted/40 px-3 py-2 text-sm text-foreground">
      <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <span>
        {text}
        {by === "route" && (
          <>
            {" "}
            <Link
              to={`/projects/${projectId}/settings?tab=route-patterns`}
              className="font-medium text-primary underline underline-offset-4 hover:text-foreground"
            >
              Configure route patterns in settings →
            </Link>
          </>
        )}
      </span>
    </p>
  );
}

export function NoRuns({
  filtered,
  window,
  scoped,
}: {
  filtered: boolean;
  window: boolean;
  /** The page scope narrows runs (a period or a build). */
  scoped?: boolean;
}) {
  const firstRunCopy = useFirstRunCopy();
  if (scoped) return <ScopeEmptyState />;
  if (filtered || window) {
    return (
      <EmptyState
        icon={Route}
        title="No runs match"
        description="No completed walkthrough in this window matches these filters."
        variant="compact"
      />
    );
  }
  return (
    <EmptyState
      icon={Route}
      title="Hotspots appear after your first walkthrough"
      description={firstRunCopy}
      variant="compact"
    />
  );
}
